import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { kelvinToCelsius } from '../../globals/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  CircularProgress,
  Box,
  Typography,
  TableContainer,
  TableHead,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  ErrorOutline as ErrorOutlineIcon,
  WaterDrop as WaterDropIcon,
} from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';
import CropGrowthGraph from './CropGrowthGraph';
import AmSatelliteImagery from './AmSatelliteImagery';
import AMIrrigationPlanner from './AMIrrigationPlanner';
import AMSoilMoistureGraph2 from './AMSoilMoistureGraph2';
import api from '../../globals/axios';
import GenericButton from '../Custom/GenericButton';
import { useAlert } from '../Alert';
import { getAuth } from '../../globals/authentication';
import { generatePdf } from '../../utils/amSatelliteReportDownload';
import IrrigationInsightCard from './IrrigationInsightCard';
import NoSatelliteDataSlug from './NoSatelliteDataSlug';

const AMSatelliteDataViewer = ({ farmId, polygon }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [farmSatelliteData, setFarmSatelliteData] = useState(null);
  const { showAlert } = useAlert();
  const auth = getAuth();
  const language = localStorage.getItem('i18nextLng') || 'en';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/satellitedata/get-am-satellite-data/${farmId}/`
        );
        setFarmSatelliteData(data.data);
      } catch (err) {
        console.log(err);
        setError(t('Failed to load data'));
      }
      setLoading(false);
    };

    fetchData();
  }, [farmId]);

  const handleDownloadReport = async () => {
    setReportLoading(true);
    try {
      await generatePdf(
        farmId,
        (
          farmSatelliteData?.latest_satellite_image?.date * 1000
        ).toLocaleString() || new Date().toLocaleDateString(),
        auth?.token,
        language,
        setReportLoading
      );
    } catch (error) {
      showAlert('error', t('Failed to generate report'));
      console.error('Error generating PDF:', error);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          my: 4,
          py: 3,
        }}
      >
        <CircularProgress
          size={40}
          thickness={4}
          sx={{ mb: 2, color: 'primary.main' }}
        />
        <Typography variant="h6" color="text.secondary">
          {t('Loading satellite data')}...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('This may take a moment')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {error ? (
        <Box
          sx={{
            p: 3,
            my: 2,
            border: '1px solid #f5c6cb',
            borderRadius: 2,
            bgcolor: '#f8d7da',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ErrorOutlineIcon sx={{ color: '#721c24', mr: 2, fontSize: 28 }} />
          <Box>
            <Typography
              variant="h6"
              sx={{ color: '#721c24', fontWeight: 'bold', mb: 0.5 }}
            >
              {t('Data Loading Error')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#721c24' }}>
              {error}
            </Typography>
          </Box>
        </Box>
      ) : null}
      {farmSatelliteData?.latest_satellite_image ? (
        <div>
          {/* Satellite Map Overlay */}
          <AmSatelliteImagery
            image_data={farmSatelliteData.latest_satellite_image}
            polygon={polygon}
          />
          {/* Irrigation Insight Card */}
          {farmSatelliteData.insights && (
            <IrrigationInsightCard
              insights={farmSatelliteData.insights}
              date={farmSatelliteData?.latest_satellite_image?.dt * 1000}
            />
          )}

          {/* Soil Moisture Analysis, Trend Graph, and 7 Day Irrigation Planner Card */}
          <Box
            sx={{
              mt: 3,
              mb: 3,
              p: 0,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid #e0e0e0',
              bgcolor: '#fafbfc',
            }}
          >
            {/* Soil Moisture Analysis */}
            <Box sx={{ textAlign: 'center', my: 3 }}>
              <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                {t('Soil Moisture Analysis')}
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              sx={{ mb: 2 }}
            >
              <WaterDropIcon color="primary" fontSize="small" />
              <Typography variant="body1" fontWeight="medium">
                {t('Current Soil Moisture')}:{' '}
                {`${farmSatelliteData.insights.soil_moisture.toFixed(2)}%`}
              </Typography>
            </Box>

            {/* Soil Moisture Trend Graph */}
            <Box sx={{ textAlign: 'center', my: 3 }}>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {t('Soil Moisture Trend')}
              </Typography>
            </Box>
            <Box sx={{ px: 2 }}>
              <AMSoilMoistureGraph2 farmId={farmId} />
            </Box>

            {/* 7 Day Irrigation Planner */}
            <Box sx={{ textAlign: 'center', my: 3 }}>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {t('7 Day Irrigation Planner')}
              </Typography>
            </Box>
            <Box sx={{ px: 2, pb: 3 }}>
              <AMIrrigationPlanner farmId={farmId} />
              <Typography variant="caption" color="text.secondary">
                {t('Last updated')}:{' '}
                {new Date(
                  farmSatelliteData?.latest_satellite_image?.dt * 1000
                ).toLocaleString()}
              </Typography>
            </Box>
          </Box>

          {/* Soil Temperature Metrics */}
          <Box sx={{ textAlign: 'center', my: 3 }}>
            <Typography variant="h6" style={{ fontWeight: 'bold' }}>
              {t('Soil Temperature Metrics')}
            </Typography>
          </Box>
          <TableContainer
            component={Paper}
            sx={{ maxWidth: 600, mx: 'auto', mt: 2 }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {t('Parameter')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {t('Value')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover>
                  <TableCell>{t('Date')}</TableCell>
                  <TableCell>
                    {new Date(
                      farmSatelliteData.soil_data_current?.dt * 1000
                    ).toLocaleDateString()}
                  </TableCell>
                </TableRow>

                <TableRow hover>
                  <TableCell>{t('Temperature (10cm underground)')}</TableCell>
                  <TableCell>{`${kelvinToCelsius(
                    farmSatelliteData.soil_data_current?.t10
                  )} °C`}</TableCell>
                </TableRow>

                <TableRow hover>
                  <TableCell>{t('Surface Temperature')}</TableCell>
                  <TableCell>{`${kelvinToCelsius(
                    farmSatelliteData.soil_data_current?.t0
                  )} °C`}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Tooltip title={t('These metrics are updated 2 times a day')}>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {t('Last updated on')}:{' '}
              {new Date(
                farmSatelliteData?.soil_data_current?.dt * 1000
              ).toLocaleString()}
            </Typography>
          </Tooltip>

          {/* Crop Growth Analysis */}
          <Box sx={{ textAlign: 'center', my: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {t('Crop Growth Analysis')}
            </Typography>
          </Box>
          <CropGrowthGraph farmId={farmId} />
          <GenericButton
            backgroundColor={'#4CAF50'}
            fontColor={'#fff'}
            fontSize={'14px'}
            padding={'7px 10px'}
            onClick={handleDownloadReport}
            disabled={reportLoading}
          >
            {reportLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <Typography>{t('Download Report')}</Typography>
            )}
          </GenericButton>
        </div>
      ) : (
        <NoSatelliteDataSlug />
      )}
    </>
  );
};

export default AMSatelliteDataViewer;
